import PRODUCT_API from '../utilities/api/product'
import CART_API, { cancelCheckToken, cancelInitCart } from '../utilities/api/cart'
import ACCOUNT_API from '../utilities/api/account'

import { getCartToken, removeToken, removeTokenWhenFatalError, updateToken } from '../utilities/function/cartToken'

import {
    errorMessagesModifier,
    getDataFromSuccessResponse,
    getErrorFromFailResponse,
    isPromoRemovedFromResponse,
    isResponseCancelled
} from '../utilities/function/apiResponse'

import {
    generatePayloadFromConfig,
    getAttributes,
    getUpdateProductConfigPayload
} from '../utilities/function/configuration'

import { getDataFromURL } from '../utilities/function/urlparams'

import { generateProductPayloadConfig, getAddedConfigurations } from '../utilities/function/manageServiceTypes'
import { PAYPAL } from '../components/shared/paymentoptions'
import globalCookies from '../utilities/function/cookies'


export const SET_FADEOUT_APP = 'app/SET_FADEOUT_APP'
export const UPDATE_CONFIG = 'app/UPDATE_CONFIG'
export const DELETE_DOMAIN_REQUEST = 'app/DELETE_DOMAIN_REQUEST'
export const DELETE_DOMAIN_SUCCESS = 'app/DELETE_DOMAIN_SUCCESS'
export const GET_SELECTED_PRODUCT_LOADING = 'app/GET_SELECTED_PRODUCT_LOADING'
export const GET_SELECTED_PRODUCT_ERROR = 'app/GET_SELECTED_PRODUCT_ERROR'
export const GET_SELECTED_PRODUCT_SUCCESS = 'app/GET_SELECTED_PRODUCT_SUCCESS'
export const GET_SELECTED_PRODUCT_RESET = 'app/GET_SELECTED_PRODUCT_RESET'
export const CART_LOADING = 'app/CART_LOADING'
export const CART_SUCCESS = 'app/CART_SUCCESS'
export const CART_ERROR = 'app/CART_ERROR'
export const CART_ERROR_RESET = 'app/CART_ERROR_RESET'
export const CART_RESET = 'app/CART_RESET'
export const UPDATE_FINISHED = 'app/UPDATE_FINISHED'
export const UPDATE_POPUP_NOTIFICATION = 'app/UPDATE_POPUP_NOTIFICATION'
export const UPDATE_VIEWPORT = 'app/UPDATE_VIEWPORT'

export const ACCOUNT_SUCCESS = 'app/ACCOUNT_SUCCESS'
export const ACCOUNT_LOADING = 'app/ACCOUNT_LOADING'
export const ACCOUNT_ERROR = 'app/ACCOUNT_ERROR'

export const setFadeoutAppAction = (bool) => ({
    type: SET_FADEOUT_APP,
    fadeoutApp: bool
})

/*
  Note:
  Product Configuration: a state used by this app to manage all user inputs from the app
  CID : an ID to identify each of product configurations
 */

//fetch product data from api and store it in the 'Selected Product' state
export const getSelectedProduct = (pid, dispatch, getSelectedProductStatus = undefined, promo = null) => {
    //only set state to loading if the value is not loading
    if (getSelectedProductStatus !== 'loading') {
        dispatch({
            type: GET_SELECTED_PRODUCT_LOADING
        })
    }
    //call api and set the state based on the response
    PRODUCT_API.getProduct(pid, promo).then(response => {
        const getSelectedProductListData = getDataFromSuccessResponse(response)
        const { length } = getSelectedProductListData
        let getSelectedProductData = null
        if (getSelectedProductListData && length > 0) {
            getSelectedProductData = getSelectedProductListData[0]
        }
        if (getSelectedProductData && parseInt(pid) === getSelectedProductData.id) {
            dispatch({
                type: GET_SELECTED_PRODUCT_SUCCESS,
                getSelectedProductData
            })
        }
    }, error => {
        const getSelectedProductError = getErrorFromFailResponse(error)
        if (!isResponseCancelled(getSelectedProductError)) {
            dispatch({
                type: GET_SELECTED_PRODUCT_ERROR,
                getSelectedProductError
            })
            window.setTimeout(() => {
                updatePopupNotification(dispatch, {
                    status: 'error',
                    error: getSelectedProductError
                })
            }, 300)
        }
    })
}

//reset selected product state
export const resetSelectedProduct = (dispatch) => {
    dispatch({
        type: GET_SELECTED_PRODUCT_RESET
    })
}

//set or update product config state based on cid (cid can be product id or uuid)
export const updateConfig = (configurations, cid, config, dispatch) => {
    const productConfig = configurations && configurations[cid] ? configurations[cid] : {}
    const newProductConfig = {
        ...productConfig,
        ...config
    }
    let newConfig = configurations
    newConfig[cid] = newProductConfig
    if (newConfig) {
        localStorage.configurations = JSON.stringify(newConfig)
    }
    dispatch({
        type: UPDATE_CONFIG,
        configurations: newConfig
    })
}

//set or replace all product configurations at once
export const loadConfiguration = (configurations, dispatch) => {
    localStorage.configurations = JSON.stringify(configurations)
    dispatch({
        type: UPDATE_CONFIG,
        configurations
    })
}

//get product configurations from local storage (only the ones haven't been added to cart)
const getLocalConfigurations = (cartItems = null) => {
    if (localStorage.configurations) {
        const filtered = {}
        let configurations = {}
        try {
            configurations = JSON.parse(localStorage.configurations)
        } catch (e) {
            configurations = {}
        }
        if (configurations && Object.keys(configurations).length) {
            //exclude upsell and items in the cart
            Object.keys(configurations).forEach(key => {
                if (configurations[key] && configurations[key].selectedProduct && !configurations[key].upsell && !configurations[key].uuid) {
                    //exclude all domain configs related to any items in the cart
                    if (cartItems && cartItems.length && Array.isArray(cartItems) && configurations[key].domainSearchResult) {
                        const { domain, tld } = configurations[key].domainSearchResult
                        const name = domain + tld
                        const inCart = cartItems.find(item => item.name === name)
                        if (!inCart) {
                            filtered[key] = configurations[key]
                        }
                    } else {
                        filtered[key] = configurations[key]
                    }
                }
            })
        }
        configurations = filtered
        return configurations
    }
    return {}
}

//update product configurations from api response (add,edit,delete,get)
export const syncConfiguration = (successResponse, dispatch, history = null, redirect = null, removed = null, callback = null) => {
    if (successResponse) {
        const { cart, message } = successResponse
        if (cart && cart.items && cart.items.length > 0) {
            const { items } = cart
            const getGroupIDs = () => {
                const ids = []
                items.forEach(item => {
                    const hasId = ids.some((id) => (id === item.id))
                    if (!hasId) {
                        ids.push(item.id)
                    }
                })
                return ids
            }
            const getPromo = () => {
                let promo = null
                items.forEach(item => {
                    const { discount } = item
                    if (discount) {
                        promo = discount
                    }
                })
                return promo
            }
            const uniqueIds = getGroupIDs()
            PRODUCT_API.getProduct(uniqueIds, getPromo()).then(response => {
                const productInformation = getDataFromSuccessResponse(response)
                let configurations = getLocalConfigurations(cart.items)
                let addedConfigurations = getAddedConfigurations(configurations, productInformation, items)
                const newconfigurations = {
                    ...configurations,
                    ...addedConfigurations
                }
                if (removed && removed.length > 0) {
                    removed.forEach(item => {
                        const { name, id, order_type } = item
                        if (order_type) {
                            const { name } = item
                            delete newconfigurations[name]
                        } else {
                            if (item.upsell) {
                                if (newconfigurations[id + name]) {
                                    delete newconfigurations[id + name]
                                }
                            } else {
                                delete newconfigurations[id]
                            }
                        }
                    })
                }
                if (callback) {
                    callback()
                }

                loadConfiguration(newconfigurations, dispatch)
                if (history && redirect) {
                    history.push(`${history.location.pathname}${redirect}`)
                }
                dispatch({
                    type: CART_SUCCESS,
                    cartData: cart
                })
            }, error => {
                console.log(error)
            })
        } else {
            if (message === 'No token or active cart found') {
                removeToken()
            } else {
                loadConfiguration(getLocalConfigurations(), dispatch)
            }
            if (history && redirect) {
                history.push(`${history.location.pathname}${redirect}`)
            }
            dispatch({
                type: CART_SUCCESS,
                cartData: cart
            })
        }
    }
}

//call add product from api and update product configurations
export const addItemToCart = (productConfig, dispatch, history, configurations, location = null, callback = null) => {
    const currentToken = getCartToken()
    const items = generatePayloadFromConfig(productConfig, configurations)
    dispatch({
        type: CART_LOADING
    })
    const getPromo = () => {
        if (location && location.search && getCartToken() && getDataFromURL(location.search)) {
            const { promo } = getDataFromURL(location.search)
            if (promo) {
                return promo
            }
        }
        return false
    }
    const handleSuccess = (response) => {
        const responseData = getDataFromSuccessResponse(response)
        const { token } = responseData
        updateToken(token)
        const afterSuccessCallback = () => {
            syncConfiguration(responseData, dispatch, history, '?a=checkout', items, callback)
            resetSelectedProduct(dispatch)
        }
        if (getPromo()) {
            CART_API.addPromo(getPromo(), getCartToken()).then(successresponse => {
                const data = getDataFromSuccessResponse(successresponse)
                const payload = {
                    status: 'success',
                    data,
                    error: null
                }
                updatePopupNotification(dispatch, payload)
                //reset after add promo
                syncConfiguration(data, dispatch, history, '?a=checkout', items)
                resetSelectedProduct(dispatch)
            }, errorresponse => {
                const error = getErrorFromFailResponse(errorresponse)
                const payload = {
                    status: 'error',
                    data: null,
                    error
                }
                updatePopupNotification(dispatch, payload)
                afterSuccessCallback()
                if (!isResponseCancelled(error)) {
                    updatePopupNotification(dispatch, {
                        status: 'error',
                        error: error
                    })
                    window.setTimeout(() => {
                        history.push(`${history.location.pathname}?a=checkout`)
                    }, 300)
                }
            })
        } else {
            afterSuccessCallback()
        }
    }
    const handleError = (error) => {
        if (callback) {
            callback()
        }
        const addCartError = getErrorFromFailResponse(error)
        const newAddCartError = errorMessagesModifier(productConfig, addCartError)
        if (!isResponseCancelled(addCartError)) {
            if (newAddCartError.status !== 400) {
                removeTokenWhenFatalError(addCartError)
                dispatch({
                    type: CART_ERROR_RESET,
                    cartError: newAddCartError
                })
            } else {
                dispatch({
                    type: CART_ERROR,
                    cartError: newAddCartError
                })
            }
            updatePopupNotification(dispatch, {
                status: 'error',
                error: newAddCartError
            })
        }
    }
    const initCart = () => {
        if (cancelInitCart) {
            cancelInitCart()
        }
        CART_API.initCart(items).then(response => {
            handleSuccess(response)
        }, error => {
            handleError(error)
        })
    }
    const addToExistingCart = () => {
        CART_API.addToExisting(items, currentToken).then(response => {
            handleSuccess(response)
        }, error => {
            handleError(error)
            const errorResponse = getErrorFromFailResponse(error)
            if (errorResponse && errorResponse.status) {
                if (errorResponse.status !== 400) {
                    removeToken()
                }
            } else {
                removeToken()
            }
        })
    }
    //check if the cart has been created
    if (currentToken && currentToken !== 'undefined') {
        if (cancelCheckToken) {
            cancelCheckToken()
        }
        //check if token is valid
        CART_API.checkToken(currentToken).then(() => {
            addToExistingCart()
        }, checkTokenError => {
            const checkTokenResponseError = getErrorFromFailResponse(checkTokenError)
            if (!isResponseCancelled(checkTokenResponseError)) {
                //if not then init cart
                initCart()
            }
        })
    } else {
        //init cart
        initCart()
    }
}

//call update product from api and update product configurations
export const updateCartItem = (payload, dispatch, history, redirect = null, productConfig = null) => {
    const { uuid, items, token } = payload
    dispatch({
        type: CART_LOADING
    })
    CART_API.updateItem(token, uuid, items).then((successresponse) => {
        const data = getDataFromSuccessResponse(successresponse)
        syncConfiguration(data, dispatch, history, redirect, null)
        updatePopupNotification(dispatch, {
            status: 'success',
            data,
            error: null
        })
    }, error => {
        const errorData = getErrorFromFailResponse(error)
        const newUpdateCartError = errorMessagesModifier(productConfig, errorData)
        if (!isResponseCancelled(errorData)) {
            if (newUpdateCartError.status !== 400) {
                removeTokenWhenFatalError(errorData)
                dispatch({
                    type: CART_ERROR_RESET,
                    cartError: newUpdateCartError
                })
            } else {
                dispatch({
                    type: CART_ERROR,
                    cartError: newUpdateCartError
                })
            }
            updatePopupNotification(dispatch, {
                status: 'error',
                error: newUpdateCartError
            })
        }
    })
}

//call delete product from api and update product configurations
export const deleteCartItem = (payload, dispatch, history, redirect = null) => {
    const { uuid, token } = payload
    dispatch({
        type: CART_LOADING
    })
    CART_API.deleteItem(token, uuid).then((successresponse) => {
        const data = getDataFromSuccessResponse(successresponse)
        syncConfiguration(data, dispatch, history, redirect)
    }, error => {
        const errorData = getErrorFromFailResponse(error)
        removeTokenWhenFatalError(errorData)
        dispatch({
            type: CART_ERROR_RESET,
            cartError: errorData
        })
        updatePopupNotification(dispatch, {
            status: 'error',
            error: errorData
        })
    })

}

//call get cart token api and update product configurations
export const appInit = (dispatch, history, isLoading = false, fullValidation = false) => {
    const token = getCartToken()
    if (token && token !== 'undefined') {
        if (isLoading) {
            dispatch({
                type: CART_LOADING
            })
        }
        CART_API.checkToken(token, fullValidation).then(response => {
            const successResponse = getDataFromSuccessResponse(response)
            syncConfiguration(successResponse, dispatch)
            if (isPromoRemovedFromResponse(response)) {
                updatePopupNotification(dispatch, {
                    status: 'success',
                    data: successResponse,
                    error: null
                })
            }
        }, error => {
            const errorResponse = getErrorFromFailResponse(error)
            removeToken()
            ACCOUNT_API.removeAccessToken()
            globalCookies.remove('access_token')
            dispatch({
                type: CART_ERROR,
                cartError: errorResponse
            })
            window.setTimeout(() => {
                window.location.reload()
            }, 300)
        })
    } else {
        loadConfiguration(getLocalConfigurations(), dispatch)
        dispatch({
            type: CART_SUCCESS
        })
    }
}

//call clear cart api and remove product configurations in the cart
export const clearCart = (dispatch, history) => {
    dispatch({
        type: CART_LOADING
    })
    const token = getCartToken()
    //removeToken()
    CART_API.clearCart(token).then(successresponse => {
        const cartData = getDataFromSuccessResponse(successresponse)
        dispatch({
            type: CART_SUCCESS,
            data: cartData
        })
        updatePopupNotification(dispatch, {
            status: 'success',
            data: cartData
        })
        dispatch(setFadeoutAppAction(true))
        window.setTimeout(() => {
            history.push(`${history.location.pathname}`)
            const configurations = {}
            loadConfiguration(configurations, dispatch)
            dispatch(setFadeoutAppAction(false))
        }, 300)
    }, errorresponse => {
        const error = getErrorFromFailResponse(errorresponse)
        history.push(`${history.location.pathname}`)
        dispatch({
            type: CART_ERROR_RESET,
            cartError: error
        })
        updatePopupNotification(dispatch, {
            status: 'error',
            error: error
        })
    })
}

//remove all upsells product configurations that has a specific domain name
export const deleteAllProductConfigRelatedToDomain = (domainname, configurations, dispatch) => {
    dispatch({
        type: DELETE_DOMAIN_REQUEST
    })
    if (configurations && Object.keys(configurations).length && Object.keys(configurations).length > 0) {
        let newConfigurations = {}
        Object.keys(configurations).forEach(key => {
            if (configurations[key] && configurations[key].upsell && !configurations[key].uuid) {
                if (configurations[key].name !== domainname) {
                    newConfigurations[key] = configurations[key]
                }
            } else {
                newConfigurations[key] = configurations[key]
            }
        })
        localStorage.setItem('configurations', JSON.stringify(newConfigurations))
        dispatch({
            type: UPDATE_CONFIG,
            configurations: newConfigurations
        })

        setTimeout(() => {
            dispatch({
                type: DELETE_DOMAIN_SUCCESS,
                deleteDomainNameData: domainname
            })
        }, 1000)
    }
}

//update hosting product and add a domain (this function is used on "Edit Product" page
export const editProductPage = (cid, configurations, cartData, dispatch, location, history) => {
    if (getCartToken()) {
        const getDomainName = () => {
            if (configurations[cid] && configurations[cid].name) {
                return configurations[cid].name
            }
            return null
        }
        const getHostingID = () => {
            if (configurations[cid] && configurations[cid].selectedProduct) {
                return configurations[cid].selectedProduct.id
            }
            return null
        }
        const getDomainNameID = () => {
            if (getDomainName() && configurations[getDomainName()] && configurations[getDomainName()].selectedProduct) {
                return configurations[getDomainName()].selectedProduct.id
            }
            return null
        }
        const getPayloadFromProductID = (id, productPayloads) => {
            if (productPayloads && productPayloads.length) {
                return productPayloads.find(payload => payload.id === id)
            }
            return null
        }

        const payloadHosting = getUpdateProductConfigPayload(configurations[cid], cartData, cid)
        //check if there is a domain name in the product configuration
        if (getDomainNameID()) {
            //get domain & hosting the payloads from the product configuration
            const productPayloads = generateProductPayloadConfig(configurations[cid], configurations)
            const hostingPayload = getPayloadFromProductID(getHostingID(), productPayloads)
            const domainPayload = getPayloadFromProductID(getDomainNameID(), productPayloads)
            if (domainPayload) {
                const validateDomain = () => {
                    const domainConfig = configurations[getDomainName()]
                    //validate
                    const { order_type } = domainConfig
                    if (order_type === "transfer") {
                        if (domainConfig && domainConfig.selectedProduct && getAttributes(domainConfig.selectedProduct)) {
                            const { meta } = getAttributes(domainConfig.selectedProduct)
                            if (meta && meta.length) {
                                const isRequiredEpp = meta.find(i => i.identifier === "require_epp_code")
                                if (isRequiredEpp && !domainConfig.epp) {
                                    return false
                                }
                            }
                        }
                    } else if (order_type === "register") {
                        if (domainConfig && domainConfig.selectedProduct && getAttributes(domainConfig.selectedProduct)) {
                            const { custom_fields } = getAttributes(domainConfig.selectedProduct)
                            if (custom_fields && domainConfig.custom_fields) {
                                if (custom_fields.length !== domainConfig.custom_fields.length) {
                                    return false
                                }
                            }
                        }
                    }
                    return true
                }
                if (validateDomain()) {
                    hostingPayload['action'] = 'edit'
                    hostingPayload['uuid'] = cid
                    let items = [hostingPayload, domainPayload]
                    bulkActionCart(dispatch, history, items, '?a=checkout')
                } else {
                    updateCartItem(payloadHosting, dispatch, history, '?a=checkout', configurations[cid])
                }
            } else {
                updateCartItem(payloadHosting, dispatch, history, '?a=checkout', configurations[cid])
            }
        } else {
            updateCartItem(payloadHosting, dispatch, history, '?a=checkout', configurations[cid])
        }
    } else {
        history.push(`${history.location.pathname}`)
    }
}

//update 'finished' state (this state is used on "Complete" page
export const updateFinished = (dispatch, finished) => {
    if (finished && finished.payInvoicePayload && finished.payInvoicePayload.payment_option) {
        const { payInvoicePayload } = finished
        const { payment_option } = payInvoicePayload
        if (payment_option === PAYPAL) {
            localStorage.setItem('paypalOrder', JSON.stringify(finished))
        }
        dispatch({
            type: UPDATE_FINISHED,
            finished
        })
    }

}

//update popup notification state
export const updatePopupNotification = (dispatch, popupNotification) => {
    dispatch({
        type: UPDATE_POPUP_NOTIFICATION,
        popupNotification
    })
}

//reset popup notification
export const resetPopupNotification = (dispatch) => {
    dispatch({
        type: UPDATE_POPUP_NOTIFICATION,
        popupNotification: {}
    })
}

//delete product configuration based on CID
export const deleteConfigurationByCID = (configurations, cid, dispatch) => {
    const newConfigurations = {}

    let input = cid
    if (typeof input === "number") {
        input = input.toString()
    }

    if (configurations && Object.keys(configurations).length) {
        Object.keys(configurations).forEach(key => {
            if (key !== input) {
                newConfigurations[key] = configurations[key]
            }
        })
    }
    localStorage.configurations = JSON.stringify(newConfigurations)
    dispatch({
        type: UPDATE_CONFIG,
        configurations: newConfigurations
    })
}

//update viewport state
export const updateViewport = (dispatch, currentWidth) => {
    const xs_max = 767
    const sm_min = 768
    const sm_max = 991
    const md_min = 992
    const md_max = 1199
    const lg_min = 1200
    let viewport = undefined
    if (currentWidth <= xs_max) {
        viewport = 'xs'
    } else if (currentWidth >= sm_min && currentWidth <= sm_max) {
        viewport = 'sm'
    } else if (currentWidth >= md_min && currentWidth <= md_max) {
        viewport = 'md'
    } else if (currentWidth >= lg_min) {
        viewport = 'lg'
    }
    dispatch({
        type: UPDATE_VIEWPORT,
        viewport
    })
}

//reset cart state only
export const resetCart = (dispatch) => {
    dispatch({
        type: CART_RESET
    })
}

//go back to the website
export const closeCallback = (dispatch, history) => {
    dispatch(setFadeoutAppAction(true))
    window.setTimeout(() => {
        dispatch(setFadeoutAppAction(false))
        history.push(history.location.pathname)
    }, 300)
}

//bulk action
export const bulkActionCart = (dispatch, history, items, redirect = null) => {
    const token = getCartToken()
    dispatch({
        type: CART_LOADING
    })
    CART_API.bulkAction(token, items).then(successresponse => {
        const data = getDataFromSuccessResponse(successresponse)
        syncConfiguration(data, dispatch, history, redirect, null)
        updatePopupNotification(dispatch, {
            status: 'success',
            data,
            error: null
        })
    }, error => {
        const errorData = getErrorFromFailResponse(error)
        if (!isResponseCancelled(errorData)) {
            if (errorData.status !== 400) {
                removeTokenWhenFatalError(errorData)
                dispatch({
                    type: CART_ERROR_RESET,
                    cartError: errorData
                })
            } else {
                dispatch({
                    type: CART_ERROR,
                    cartError: errorData
                })
            }
            updatePopupNotification(dispatch, {
                status: 'error',
                error: errorData
            })
        }
    })
}

export const logoutApp = (dispatch, history) => {
    dispatch({
        type: ACCOUNT_LOADING
    })
    ACCOUNT_API.logout().then(successresponse => {
        const logoutData = getDataFromSuccessResponse(successresponse)
        dispatch({
            type: ACCOUNT_SUCCESS
        })
        updatePopupNotification(dispatch, {
            status: 'success',
            logoutData,
            error: null
        })
        removeToken()
        resetCart(dispatch)
        ACCOUNT_API.removeAccessToken()
        globalCookies.remove("access_token")
        appInit(dispatch, history)
        window.setTimeout(() => {
            dispatch(setFadeoutAppAction(true))
            window.setTimeout(() => {
                dispatch(setFadeoutAppAction(false))
                history.push(history.location.pathname)
            }, 300)
        }, 300)
    }, errorresponse => {
        const logoutError = getErrorFromFailResponse(errorresponse)
        if (!isResponseCancelled(logoutError)) {
            dispatch({
                type: ACCOUNT_ERROR
            })
        }
        updatePopupNotification(dispatch, {
            status: 'error',
            data: null,
            error: logoutError
        })
    })
}